import type Account from '@/data/account/model';
import type Plan from '@/data/plan/model';
import type User from '@/data/user/model';
import { MenuItem } from '@/shared/components/nav/MenuItem';
import MenuItems from '@/shared/components/nav/MenuItems';
import Features from '@/shared/features';
import { posthogCapture } from '@/shared/vendor/posthog';

type WorkplaceMenuProps = {
  authUser: User;
  authAccount: Account;
  accountPlan: Plan;
  trackDocumentsNav: () => void;
  pt121WorkplaceObjects: boolean;
  upsellMultipleSchedules: (e: Event) => void;
  canViewDocStorage: boolean;
};

const WorkplaceMenuItem = ({
  authUser,
  authAccount,
  accountPlan,
  trackDocumentsNav,
  pt121WorkplaceObjects,
  upsellMultipleSchedules,
  canViewDocStorage,
}: WorkplaceMenuProps) => {
  const WORKPLACE_ID_PREFIX = 'top_nav.workplace.';

  const isSingleLocation =
    accountPlan.isSingleLocation2025() || accountPlan.isSingleLocation2025V2() || accountPlan.isSingleLocation2025V3();

  function trackInteraction(label: string) {
    posthogCapture(WORKPLACE_ID_PREFIX + label);
  }

  return (
    <MenuItem
      className="workplace menu-icon"
      icon="workplace"
      label="Workplace"
      id="app-settings-menu"
      hideLabel="desktop"
      dropdown
    >
      <MenuItems dropdownMenu>
        {pt121WorkplaceObjects ? (
          <MenuItem icon="person" label="Users" to="/users" onClick={() => trackInteraction('users')} />
        ) : (
          <MenuItem icon="person" label="Users" to="/employees" onClick={() => trackInteraction('users')} />
        )}

        <MenuItem icon="positions" label="Positions" to="/positions" onClick={() => trackInteraction('positions')} />

        {authAccount.getSettings('schedule.worktags') && (
          <MenuItem icon="tags" label="Tags" to="/tags" onClick={() => trackInteraction('tags')} />
        )}

        <MenuItem
          icon="schedules"
          label="Schedules"
          show={
            (authAccount.hasFeature(Features.LOCATIONS) || authAccount.isReverseTrial() || isSingleLocation) &&
            authUser.canManage()
          }
          to="/locations"
          onClick={() => trackInteraction('schedules')}
        />

        {/* Menu item for UPGRADES, reverse trial uses the regular schedules button */}
        <MenuItem
          className="disable upsell"
          icon="schedules"
          label="Schedules"
          onClick={(e: Event) => {
            trackInteraction('schedules');
            upsellMultipleSchedules(e);
          }}
          show={
            !authAccount.hasFeature(Features.LOCATIONS) &&
            !authAccount.isReverseTrial() &&
            !isSingleLocation &&
            authUser.canManage()
          }
        />

        {isSingleLocation && (
          <MenuItem
            icon="job-site"
            label="Job Sites"
            to="/sites"
            postIcon="upgrade"
            postIconColor="#C59A27"
            onClick={() => trackInteraction('job_sites')}
          />
        )}

        <MenuItem
          icon="job-site"
          label="Job Sites"
          show={authAccount.hasFeature(Features.SITES) || authAccount.isReverseTrial()}
          to="/sites"
          onClick={() => trackInteraction('job_sites')}
        />

        <MenuItem
          icon="shift-template"
          label="Shift Templates"
          show={authAccount.hasScheduling()}
          to="/blocks"
          onClick={() => trackInteraction('shift_templates')}
        />

        {authAccount.getSettings('tasks.enabled') && (
          <MenuItem icon="tasks" label="Task Lists" to="/tasks" onClick={() => trackInteraction('task_list')} />
        )}

        {canViewDocStorage ? (
          <MenuItem
            icon="document"
            label="Documents"
            to="/documents"
            onClick={() => {
              trackInteraction('documents');
              trackDocumentsNav();
            }}
          />
        ) : (
          <MenuItem icon="forms" label="Forms" target="_blank" to="https://wheniwork.com/hr" />
        )}
      </MenuItems>
    </MenuItem>
  );
};

export default WorkplaceMenuItem;
